// frontend/packages/business-console/src/components/NotificationBell.jsx
//
// Tenant-facing notification bell for the Console header. Self-fetching +
// polls every 60s; shows an unread badge; opens a popover listing recent
// in-app notifications with click-to-read + mark-all-read. Mirrors the
// AiUsageMeter header-component pattern (stays quiet on error).
//
// Click-through: a notification can deep-link into the workspace. We map a
// notification's metadata to a workspace subpath - either an explicit
// metadata.link or a known metadata.kind (e.g. a ready build draft routes
// to the review page). Routes resolve under /business/:slug, so we read the
// active slug from the route params.
//
// Copy is customer-visible: no em-dashes.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';

import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../services/notificationsService';

const POLL_MS = 60000;

// Known notification kinds that deep-link to a workspace subpath (relative
// to /business/:slug). An explicit metadata.link wins over the kind map.
const KIND_SUBPATHS = {
  build_draft_ready: 'build/review',
};

function subpathFor(n) {
  const meta = (n && n.metadata) || {};
  if (typeof meta.link === 'string' && meta.link) {
    return meta.link.replace(/^\/+/, '');
  }
  if (meta.kind && KIND_SUBPATHS[meta.kind]) return KIND_SUBPATHS[meta.kind];
  return null;
}

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const { slug } = useParams();

  const load = useCallback(async () => {
    try {
      const data = await getNotifications();
      setItems(Array.isArray(data?.items) ? data.items : []);
      setUnread(Number(data?.unread) || 0);
    } catch (e) {
      /* stay quiet in the header on error */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    load();
  };
  const handleClose = () => setAnchorEl(null);

  const handleMarkAll = async () => {
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await markAllNotificationsRead();
    } catch (e) {
      /* optimistic; a reload will reconcile */
    }
  };

  const handleItemClick = async (n) => {
    if (!n.read) {
      setItems((prev) =>
        prev.map((x) => (x._id === n._id ? { ...x, read: true } : x)),
      );
      setUnread((u) => Math.max(0, u - 1));
      try {
        await markNotificationRead(n._id);
      } catch (e) {
        /* optimistic; a reload will reconcile */
      }
    }

    // Deep-link if this notification points somewhere actionable.
    const sub = subpathFor(n);
    if (sub && slug) {
      handleClose();
      navigate(`/business/${slug}/${sub}`);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleOpen}
          size="small"
          aria-label={
            unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'
          }
          sx={{ color: 'text.secondary' }}
        >
          <Badge badgeContent={unread} color="error" max={9}>
            <NotificationsNoneOutlinedIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxWidth: '92vw' } }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Notifications
          </Typography>
          {unread > 0 ? (
            <Button size="small" onClick={handleMarkAll}>
              Mark all read
            </Button>
          ) : null}
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {items.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                You are all caught up.
              </Typography>
            </Box>
          ) : (
            <Stack divider={<Divider />}>
              {items.map((n) => (
                <Box
                  key={n._id}
                  onClick={() => handleItemClick(n)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    bgcolor: n.read ? 'transparent' : 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: n.read ? 400 : 700 }}
                  >
                    {n.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {timeAgo(n.createdAt)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Popover>
    </>
  );
}
